import { IconButton, useTheme } from '@material-ui/core'
import {
  Background,
  ReactFlow,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Controls
} from '@xyflow/react'
import '@xyflow/react/dist/base.css'
import dagre from 'dagre'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import toastError from '../../errors/toastError'
import useQueues from '../../hooks/useQueues'
import api from '../../services/api'
import CustomNode from '../CustomNode'
import DepartmentModal from '../DepartmentModal'
import DepartmentOptionsModal from '../DepartmentOptionModal'
import { useStyles } from './style'

const DepartmentOptionsFlow = ({ queueId }) => {
  const classes = useStyles()
  const theme = useTheme()
  // const { queueId } = useParams()
  const [nodes, setNodes] = useState([])
  const [edges, setEdges] = useState([])
  const { findAll: findAllQueues } = useQueues()
  const [queuesList, setQueuesList] = useState([])

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState(null)
  const [, setParentIdState] = useState(null)

  const [isDepartmentModalOpen, setIsDepartmentModalOpen] = useState(false)
  const [departmentData, setDepartmentData] = useState(null)

  const dagreGraph = new dagre.graphlib.Graph()
  dagreGraph.setDefaultEdgeLabel(() => ({}))

  const nodeWidth = 200
  const nodeHeight = 80

  const getLayoutedElements = (nodes, edges, direction = 'TB') => {
    dagreGraph.setGraph({
      rankdir: direction,
      nodesep: 50,
      ranksep: 100,
      marginx: 0,
      marginy: 0 // Define uma margem pequena no topo
    })

    nodes.forEach(node => {
      dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight })
    })

    edges.forEach(edge => {
      dagreGraph.setEdge(edge.source, edge.target)
    })

    dagre.layout(dagreGraph)

    nodes.forEach(node => {
      const nodeWithPosition = dagreGraph.node(node.id)
      node.targetPosition = 'top'
      node.sourcePosition = 'bottom'

      node.position = {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2
      }
    })

    return { nodes, edges }
  }

  const [schedules] = useState([
    {
      weekday: 'Segunda-feira',
      weekdayEn: 'monday',
      startTime: '08:00',
      endTime: '18:00'
    },
    {
      weekday: 'Terça-feira',
      weekdayEn: 'tuesday',
      startTime: '08:00',
      endTime: '18:00'
    },
    {
      weekday: 'Quarta-feira',
      weekdayEn: 'wednesday',
      startTime: '08:00',
      endTime: '18:00'
    },
    {
      weekday: 'Quinta-feira',
      weekdayEn: 'thursday',
      startTime: '08:00',
      endTime: '18:00'
    },
    {
      weekday: 'Sexta-feira',
      weekdayEn: 'friday',
      startTime: '08:00',
      endTime: '18:00'
    },
    {
      weekday: 'Sábado',
      weekdayEn: 'saturday',
      startTime: '08:00',
      endTime: '12:00'
    },
    {
      weekday: 'Domingo',
      weekdayEn: 'sunday',
      startTime: '00:00',
      endTime: '00:00'
    }
  ])

  useEffect(() => {
    const fetchQueues = async () => {
      const list = await findAllQueues()
      setQueuesList(list)
    }
    fetchQueues()
  }, [findAllQueues])

  const fetchFlowData = async () => {
    if (!queueId) {
      setNodes([])
      setEdges([])
      return
    }
    try {
      const queuesList = await findAllQueues()
      const { data: optionsData } = await api.get(`/queue-options`, {
        params: { queueId, parentId: -1 }
      })

      const buildOptionTree = async (list, currentQId) => {
        return Promise.all(
          list.map(async option => {
            const isForward = Boolean(option.forwardQueueId)
            const targetQueueId = isForward ? option.forwardQueueId : currentQId
            const targetParentId = isForward ? -1 : option.id

            const { data: childrenData } = await api.get('/queue-options', {
              params: { queueId: targetQueueId, parentId: targetParentId }
            })

            const children = await buildOptionTree(childrenData, targetQueueId)
            return { ...option, children }
          })
        )
      }

      const optionTree = await buildOptionTree(optionsData, queueId)
      const { nodes: initialNodes, edges: initialEdges } = generateFlowData(
        optionTree,
        queuesList,
        queueId
      )
      const layoutedElements = getLayoutedElements(initialNodes, initialEdges)

      setNodes(layoutedElements.nodes)
      setEdges(layoutedElements.edges)
    } catch (err) {
      toastError(err)
    }
  }

  useEffect(() => {
    const fetchQueues = async () => {
      const list = await findAllQueues()
      setQueuesList(list)
    }
    fetchQueues()
  }, [findAllQueues])

  useEffect(() => {
    fetchFlowData()
  }, [queueId])

  const generateFlowData = (options, queuesList, currentQueueId) => {
    const newNodes = []
    const newEdges = []

    const queue = queuesList.find(q => q.id === +currentQueueId)

    if (!queue) {
      return { nodes: newNodes, edges: newEdges }
    }

    const queueNodeId = `${queue.id}`

    const hasOptions = options && options.length > 0
    const isMenuRoot = !!queue.notificationact
    console.log(
      `[Flow] Gerando Raiz ID: ${queueNodeId} | Possui Filhos: ${hasOptions}`
    )

    const hasSubChildren = Boolean(
      options.children && options.children.length > 0
    )

    newNodes.push({
      id: queueNodeId,
      data: {
        label: `${queue.name}`,
        queueId: +currentQueueId,
        isRoot: true,
        isLast: true,
        queuesList: queuesList,
        parentId: null,
        children: options || [],
        hasChildren: hasOptions,
        isMenu: !options.forwardQueueId || hasSubChildren,
        isDepartment: Boolean(options.forwardQueueId)
      },
      type: 'input',
      draggable: false,
      style: {
        backgroundColor: theme.palette.blue_300.main,
        color: theme.palette.white_100.main,
        borderRadius: '8px',
        padding: '2px 16px',
        textAlign: 'center',
        border: 'none'
      }
    })

    // const traverse = (optionsList, parentId) => {
    //   optionsList.forEach((option, index) => {
    //     const nodeId = `${option.id}`
    //     const isLastInList = index === optionsList.length - 1

    //     let nodeGreetingMessage = option.greetingMessage || ''
    //     if (option.forwardQueueId) {
    //       const forwardQueue = queuesList.find(
    //         q => q.id === option.forwardQueueId
    //       )
    //       if (forwardQueue) {
    //         nodeGreetingMessage = forwardQueue.greetingMessage || ''
    //       }
    //     }

    //     if (option.title && option.title.trim() !== '') {
    //       newNodes.push({
    //         id: nodeId,
    //         data: {
    //           // Copia todos os dados da opção original
    //           ...option,
    //           label: option.title,
    //           hasChildren: option.children && option.children.length > 0,
    //           isLast: isLastInList,
    //           queuesList: queuesList,
    //           queueId: +currentQueueId,
    //           parentId: parentId,
    //           isRoot: false,
    //           // Sobrescreve a mensagem de saudação se necessário
    //           greetingMessage: nodeGreetingMessage,
    //           isMenu: !option.forwardQueueId, // ou true se o root sempre puder ter filhos
    //           isDepartment: !!option.forwardQueueId
    //         },
    //         draggable: false,
    //         style: {
    //           backgroundColor: theme.palette.blue_300.main,
    //           color: theme.palette.white_100.main,
    //           borderRadius: '8px',
    //           padding: '2px 16px',
    //           textAlign: 'center',
    //           border: 'none'
    //         }
    //       })
    //     }

    //     newEdges.push({
    //       id: `edge-${parentId}-${nodeId}`,
    //       source: parentId,
    //       target: nodeId,
    //       animated: false,
    //       style: {
    //         stroke:
    //           theme.palette.type === 'dark'
    //             ? theme.palette.dark_50.main
    //             : theme.palette.gray_300.main
    //       },
    //       draggable: false
    //     })

    //     if (option.children && option.children.length > 0) {
    //       traverse(option.children, nodeId)
    //     }
    //   })
    // }

    const traverse = (optionsList, parentId) => {
      optionsList.forEach((option, index) => {
        const nodeId = `${option.id}`
        const isLastInList = index === optionsList.length - 1

        let nodeGreetingMessage = option.greetingMessage || ''
        if (option.forwardQueueId) {
          const forwardQueue = queuesList.find(
            q => q.id === option.forwardQueueId
          )
          if (forwardQueue) {
            nodeGreetingMessage = forwardQueue.greetingMessage || ''
          }
        }

        if (option.title && option.title.trim() !== '') {
          newNodes.push({
            id: nodeId,
            data: {
              ...option,
              label: option.title,
              hasChildren: option.children && option.children.length > 0,
              isLast: isLastInList,
              queuesList: queuesList,
              queueId: +currentQueueId,
              parentId: parentId,
              isRoot: false,
              greetingMessage: nodeGreetingMessage,
              isMenu: option.forwardQueueId
                ? !!queuesList.find(queue => queue.id === option.forwardQueueId)
                  ?.notificationact
                : true,
              isDepartment: !!option.forwardQueueId
            },
            draggable: false,
            style: {
              backgroundColor: theme.palette.blue_300.main,
              color: theme.palette.white_100.main,
              borderRadius: '8px',
              padding: '2px 16px',
              textAlign: 'center',
              border: 'none'
            }
          })
        }

        newEdges.push({
          id: `edge-${parentId}-${nodeId}`,
          source: parentId,
          target: nodeId,
          animated: false,
          style: {
            stroke:
              theme.palette.type === 'dark'
                ? theme.palette.dark_50.main
                : theme.palette.gray_300.main
          },
          draggable: false
        })

        if (option.children && option.children.length > 0) {
          traverse(option.children, nodeId)
        }
      })
    }

    if (options && options.length > 0) {
      traverse(options, queueNodeId)
    } else {
      newNodes[0].data.isLast = true
    }

    return { nodes: newNodes, edges: newEdges }
  }

  const onNodesChange = useCallback(
    changes =>
      setNodes(nodesSnapshot => applyNodeChanges(changes, nodesSnapshot)),
    []
  )

  const onEdgesChange = useCallback(
    changes =>
      setEdges(edgesSnapshot => applyEdgeChanges(changes, edgesSnapshot)),
    []
  )

  const onConnect = useCallback(
    params => setEdges(edgesSnapshot => addEdge(params, edgesSnapshot)),
    []
  )

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedOption(null)
    setParentIdState(null)
  }

  const handleSave = async values => {
    try {
      const optionToSave = { ...values }
      const isEditing = !!optionToSave.id

      if (isEditing) {
        const parentId =
          optionToSave.queueId === parseInt(optionToSave.parentId)
            ? null
            : optionToSave.parentId
        const optionToUpdate = {
          ...values,
          parentId: parentId
        }
        await api.put(`/queue-options/${optionToSave.id}`, optionToUpdate)

        if (optionToSave.forwardQueueId) {
          const queueToUpdate = {
            name: optionToSave.title,
            greetingMessage: optionToSave.greetingMessage,
            notificationact: optionToSave.notificationact
          }
          await api.put(`/queue/${optionToSave.forwardQueueId}`, queueToUpdate)
        }
        toast.success('Opção atualizada com sucesso!')

        if (optionToSave.attachmentFile) {
          const formData = new FormData()
          formData.append('file', optionToSave.attachmentFile)
          await api.post(
            `/queue-options/${optionToSave.id}/media-upload`,
            formData
          )
        }
        return optionToSave
      } else {
        const isMenuOptionDisabled = !optionToSave.menuOption
        const attachmentFile = optionToSave.attachmentFile
        delete optionToSave.attachmentFile

        let savedOptionId
        let newDepartmentId = null

        if (isMenuOptionDisabled) {
          // Lógica para criar um NOVO DEPARTAMENTO (Nível 2)
          const newQueueData = {
            name: optionToSave.title,
            greetingMessage: optionToSave.greetingMessage,
            description: optionToSave.description,
            notificationact: optionToSave.notificationact,
            isBot: false,
            mediaPath: optionToSave.mediaPath,
            mediaName: optionToSave.mediaName,
            nivel: 2
          }
          const { data: newQueue } = await api.post('/queue', {
            ...newQueueData,
            schedules
          })
          newDepartmentId = newQueue.id

          const queueOptionData = {
            title: optionToSave.title,
            option: optionToSave.option,
            exitChatbot: optionToSave.exitChatbot,
            mediaPath: optionToSave.mediaPath,
            mediaName: optionToSave.mediaName,
            queueId: optionToSave.queueId,
            forwardQueueId: newDepartmentId,
            parentId: optionToSave.parentId
          }
          const { data: newOption } = await api.post(
            '/queue-options',
            queueOptionData
          )
          savedOptionId = newOption
          console.log('saved', savedOptionId)

          if (attachmentFile) {
            const formData = new FormData()
            formData.append('file', attachmentFile)
            await api.post(
              `/queue-options/${newOption.id}/media-upload`,
              formData
            )
          }
          toast.success('Departamento e opção criados com sucesso!')
        } else {
          // Lógica para salvar uma OPÇÃO DE MENU sem criar departamento
          const { data: newOption } = await api.post(
            '/queue-options',
            optionToSave
          )
          savedOptionId = newOption
          toast.success('Opção criada com sucesso!')

          if (attachmentFile) {
            const formData = new FormData()
            formData.append('file', attachmentFile)
            await api.post(
              `/queue-options/${optionToSave.id}/media-upload`,
              formData
            )
          }
        }
        return savedOptionId
      }

      // Recarrega os dados para atualizar o ReactFlow
      // await fetchFlowData()
      // handleCloseModal()
      // return isEditing ? optionToSave : newOptionResult;
    } catch (err) {
      toastError(err)
    }
  }

  const deleteOptionRecursively = async optionId => {
    try {
      const { data: childrenOptions } = await api.get(`/queue-options`, {
        params: { queueId, parentId: optionId }
      })

      for (const childOption of childrenOptions) {
        await deleteOptionRecursively(childOption.id)

        if (childOption.forwardQueueId) {
          await api.delete(`/queue/${childOption.forwardQueueId}`)
          console.log(`Departamento ${childOption.forwardQueueId} excluído`)
        }

        await api.delete(`/queue-options/${childOption.id}`)
        console.log(`Opção ${childOption.id} excluída`)
      }
    } catch (err) {
      console.error('Erro ao deletar opções filhas:', err)
      throw err
    }
  }

  const handleDelete = useCallback(
    async optionToDelete => {
      try {
        if (optionToDelete?.id) {
          await deleteOptionRecursively(optionToDelete.id)
        }

        if (optionToDelete?.forwardQueueId) {
          await api.delete(`/queue/${optionToDelete.forwardQueueId}`)
          toast.success('Departamento principal excluído com sucesso!')
        }

        if (optionToDelete?.id) {
          await api.delete(`/queue-options/${optionToDelete.id}`)
          toast.success('Opção e todas as sub-opções excluídas com sucesso!')
        }

        await fetchFlowData()
        handleCloseModal()
      } catch (err) {
        toastError(err)
      }
    },
    [fetchFlowData, handleCloseModal, queueId]
  )

  const handleAddChild = useCallback((nodeId, nodeData) => {
    const newOption = {
      title: '',
      greetingMessage: '',
      edition: true,
      option: (nodeData.children?.length ?? 0) + 1,
      parentId: nodeId ? parseInt(nodeId) : null,
      queueId: nodeData.queueId,
      children: []
    }
    setSelectedOption(newOption)
    setParentIdState(nodeId ? parseInt(nodeId) : null)
    setIsModalOpen(true)
  }, [])

  const handleAddMainSibling = useCallback((nodeId, nodeData) => {
    const newOption = {
      title: '',
      greetingMessage: '',
      edition: true,
      option: Number(nodeData.option || 0) + 1,
      parentId: null,
      queueId: nodeData.queueId,
      children: []
    }
    setSelectedOption(newOption)
    setParentIdState(null)
    setIsModalOpen(true)
  }, [])

  const handleAddChildSibling = useCallback((nodeId, nodeData) => {
    const parentId = nodeData.parentId ? parseInt(nodeData.parentId) : null
    const newOption = {
      title: '',
      greetingMessage: '',
      edition: true,
      option: Number(nodeData.option || 0) + 1,
      parentId: parentId,
      queueId: nodeData.queueId,
      children: []
    }
    setSelectedOption(newOption)
    setParentIdState(parentId)
    setIsModalOpen(true)
  }, [])

  const handleOpenDepartmentModal = useCallback(async () => {
    try {
      const { data } = await api.get(`/queue/${queueId}`)
      const option = {
        id: data.id,
        name: data.name,
        title: data.name,
        greetingMessage: data.greetingMessage,
        exitChatbot: data.exitChatbot,
        notificationact: data.notificationact,
        mediaPath: data.mediaPath,
        mediaName: data.mediaName,
        users: data.users,
        menuDepartment: true,
        menuOption: false
      }
      setDepartmentData(option)
      setIsDepartmentModalOpen(true)
    } catch (err) {
      toastError(err)
    }
  }, [queueId])

  const handleCloseDepartmentModal = () => {
    setIsDepartmentModalOpen(false)
    setDepartmentData(null)
    fetchFlowData() // Opcional: recarregar os dados após a edição
  }

  const handleNodeClick = useCallback(
    async (event, node) => {
      if (event.target.closest('button')) return

      if (node.data.isRoot) {
        handleOpenDepartmentModal()
      } else if (node.data.forwardQueueId) {
        try {
          const { data } = await api.get(`/queue/${node.data.forwardQueueId}`)
          setDepartmentData(data)
          setIsDepartmentModalOpen(true)
        } catch (err) {
          toastError(err)
        }
      } else {
        setSelectedOption(node.data)
        setIsModalOpen(true)
      }
    },
    [handleOpenDepartmentModal]
  )

  const nodeTypes = {
    default: nodeProps => (
      <CustomNode
        {...nodeProps}
        onAddChild={handleAddChild}
        onAddSibling={handleAddChildSibling}
        onAddMain={handleAddMainSibling}
      />
    )
  }

  const showFloatingButton =
    nodes.length === 1 && nodes[0].data.isRoot && !nodes[0].data.hasChildren

  return (
    <div className={classes.boxFlow}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={handleNodeClick}
        fitView
        fitViewOptions={{
          padding: 0.2,
          includeHiddenNodes: false,
          minZoom: 1,
          maxZoom: 1.2,
          duration: 800
        }}
        zoomOnScroll={false}
        panOnScroll={true}
        selectionKeyCode="Control"
        zoomActivationKeyCode="Control"
      >
        <Background variant="dots" gap={12} size={1} />
        <Controls
          showInteractive={false}
          position="top-center"
          orientation="horizontal"
          fitViewOptions={{ padding: 0.4 }} // Margem ao dar o fit
        />
      </ReactFlow>

      {nodes.length === 1 &&
        nodes[0]?.data?.isMenu &&
        !nodes[0]?.data?.hasChildren && (
          <div className={classes.addButtonContainer}>
            <IconButton
              className={classes.addButton}
              variant="contained"
              onClick={() => {
                const rootNode = nodes[0].data
                handleAddMainSibling(nodes[0].id, rootNode)
              }}
            >
              +
            </IconButton>
          </div>
        )}

      <DepartmentOptionsModal
        open={isModalOpen}
        onClose={handleCloseModal}
        option={selectedOption}
        queues={queuesList}
        onSave={handleSave}
        onDeleteOption={handleDelete}
        onFlowUpdate={fetchFlowData}
      />

      <DepartmentModal
        open={isDepartmentModalOpen}
        onClose={handleCloseDepartmentModal}
        option={departmentData}
        onFlowUpdate={fetchFlowData}
      />
    </div>
  )
}

export default DepartmentOptionsFlow
